import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '@/api/client';
import { extractErrorMessage } from '@/api/errors';
import { useAuth } from '@/auth/useAuth';

interface PendingOrder {
  reference: string;
  total_price: string;
  delivered_at: string | null;
  customer_confirmed_at: string | null;
}

interface PendingSettlementGroup {
  store_id: number;
  store_name: string | null;
  owner_email: string | null;
  order_count: number;
  total_amount: number;
  oldest_delivered_at: string | null;
  orders: PendingOrder[];
}

interface PendingConsultantAppointment {
  id: number;
  fee: string | null;
  completed_at: string | null;
}

interface PendingConsultantGroup {
  consultant_id: number;
  consultant_user_id: number;
  consultant_name: string;
  consultant_email: string;
  appointment_count: number;
  total_amount: number;
  oldest_completed_at: string | null;
  appointments: PendingConsultantAppointment[];
}

interface PendingSettlementsResponse {
  data: PendingSettlementGroup[];
  summary: {
    seller_count: number;
    order_count: number;
    total_amount: number;
  };
  consultations?: {
    data: PendingConsultantGroup[];
    summary: {
      consultant_count: number;
      appointment_count: number;
      total_amount: number;
    };
  };
}

function formatNaira(value: number | string) {
  const n = typeof value === 'number' ? value : Number(value);
  if (!Number.isFinite(n)) return String(value);
  return new Intl.NumberFormat('en-NG', {
    style: 'currency',
    currency: 'NGN',
    maximumFractionDigits: 0,
  }).format(n);
}

function formatDate(iso: string | null | undefined) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}

export function SettlementsPage() {
  const { hasPermission } = useAuth();
  const canSettle = hasPermission('orders.settle');
  const canViewEscrow = hasPermission('escrow.view');

  const [data, setData] = useState<PendingSettlementsResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<Set<number>>(new Set());
  const [expandedConsultants, setExpandedConsultants] = useState<Set<number>>(
    new Set()
  );
  const [actingStoreId, setActingStoreId] = useState<number | null>(null);
  const [actingConsultantId, setActingConsultantId] = useState<number | null>(
    null
  );

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.get<PendingSettlementsResponse>(
        '/back-office/settlements/pending'
      );
      setData(res.data);
    } catch (err: unknown) {
      setError(extractErrorMessage(err, 'Failed to load settlements'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const toggleExpanded = (storeId: number) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(storeId)) next.delete(storeId);
      else next.add(storeId);
      return next;
    });
  };

  const settleSeller = async (group: PendingSettlementGroup) => {
    if (!canSettle) return;
    if (
      !window.confirm(
        `Settle ${group.order_count} order${group.order_count === 1 ? '' : 's'} for ${group.store_name ?? `store #${group.store_id}`} (${formatNaira(group.total_amount)})? This releases funds to the seller for every eligible delivered order.`
      )
    )
      return;
    setActingStoreId(group.store_id);
    try {
      const res = await api.post<{
        settled_count: number;
        already_settled_count: number;
        failed: { reference: string; reason: string }[];
      }>('/back-office/settlements/settle-seller', {
        store_id: group.store_id,
      });
      const parts: string[] = [`Settled ${res.data.settled_count}.`];
      if (res.data.already_settled_count > 0) {
        parts.push(
          `${res.data.already_settled_count} already settled by another path.`
        );
      }
      if (res.data.failed.length > 0) {
        parts.push(`${res.data.failed.length} failed.`);
      }
      if (res.data.already_settled_count > 0 || res.data.failed.length > 0) {
        const failureDetail = res.data.failed.length
          ? '\n\n' +
            res.data.failed
              .slice(0, 5)
              .map((f) => `${f.reference}: ${f.reason}`)
              .join('\n')
          : '';
        window.alert(parts.join(' ') + failureDetail);
      }
      await load();
    } catch (err: unknown) {
      window.alert(extractErrorMessage(err, 'Failed to settle seller'));
    } finally {
      setActingStoreId(null);
    }
  };

  const toggleConsultantExpanded = (consultantId: number) => {
    setExpandedConsultants((prev) => {
      const next = new Set(prev);
      if (next.has(consultantId)) next.delete(consultantId);
      else next.add(consultantId);
      return next;
    });
  };

  const settleConsultant = async (group: PendingConsultantGroup) => {
    if (!canSettle) return;
    if (
      !window.confirm(
        `Settle ${group.appointment_count} appointment${group.appointment_count === 1 ? '' : 's'} for ${group.consultant_name || `consultant #${group.consultant_id}`} (${formatNaira(group.total_amount)})? This releases funds to the consultant.`
      )
    )
      return;
    setActingConsultantId(group.consultant_id);
    try {
      const res = await api.post<{
        settled_count: number;
        already_settled_count: number;
        failed: { id: number; reason: string }[];
      }>('/back-office/settlements/settle-consultant', {
        consultant_id: group.consultant_id,
      });
      const parts: string[] = [`Settled ${res.data.settled_count}.`];
      if (res.data.already_settled_count > 0) {
        parts.push(
          `${res.data.already_settled_count} already settled by another path.`
        );
      }
      if (res.data.failed.length > 0) {
        parts.push(`${res.data.failed.length} failed.`);
      }
      if (res.data.already_settled_count > 0 || res.data.failed.length > 0) {
        const failureDetail = res.data.failed.length
          ? '\n\n' +
            res.data.failed
              .slice(0, 5)
              .map((f) => `#${f.id}: ${f.reason}`)
              .join('\n')
          : '';
        window.alert(parts.join(' ') + failureDetail);
      }
      await load();
    } catch (err: unknown) {
      window.alert(extractErrorMessage(err, 'Failed to settle consultant'));
    } finally {
      setActingConsultantId(null);
    }
  };

  return (
    <div className="px-8 py-6">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-slate-800">Settlements</h1>
          <p className="text-sm text-slate-500">
            Eligible delivered orders grouped by seller. Settling a group
            releases funds for every order in it.
          </p>
          <Link
            to="/settlements/failed"
            className="mt-1 inline-block text-sm text-brand-600 hover:underline"
          >
            View failed payouts →
          </Link>
        </div>
        {data && (
          <div className="flex items-center gap-3 text-sm">
            <SummaryStat
              label="Sellers"
              value={
                data.summary.seller_count +
                (data.consultations?.summary.consultant_count ?? 0)
              }
            />
            <SummaryStat
              label="Items"
              value={
                data.summary.order_count +
                (data.consultations?.summary.appointment_count ?? 0)
              }
            />
            <SummaryStat
              label="Total owed"
              value={formatNaira(
                data.summary.total_amount +
                  (data.consultations?.summary.total_amount ?? 0)
              )}
              tone="emerald"
            />
          </div>
        )}
      </div>

      {canViewEscrow && <EscrowBalanceCard />}

      {loading && <div className="mt-6 text-sm text-slate-500">Loading…</div>}
      {!loading && error && (
        <div className="mt-6 rounded bg-red-50 px-4 py-3 text-sm text-red-700">
          {error}
        </div>
      )}
      {!loading &&
        !error &&
        data &&
        data.data.length === 0 &&
        (data.consultations?.data.length ?? 0) === 0 && (
          <div className="mt-6 rounded border border-slate-200 bg-white px-4 py-12 text-center text-sm text-slate-500">
            Nothing waiting to settle right now.
          </div>
        )}

      {!loading && !error && data && data.data.length > 0 && (
        <div className="mt-6 space-y-3">
          <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-500">
            Stores ({data.summary.seller_count}) ·{' '}
            {formatNaira(data.summary.total_amount)}
          </h2>
          {data.data.map((group) => (
            <section
              key={group.store_id}
              className="rounded border border-slate-200 bg-white"
            >
              <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-100 px-5 py-4">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 text-sm">
                    <span className="font-semibold text-slate-800">
                      {group.store_name ?? `Store #${group.store_id}`}
                    </span>
                    {group.owner_email && (
                      <span className="truncate text-xs text-slate-500">
                        {group.owner_email}
                      </span>
                    )}
                  </div>
                  <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-slate-500">
                    <span>
                      {group.order_count} order
                      {group.order_count === 1 ? '' : 's'} ·{' '}
                      <span className="font-medium text-slate-800">
                        {formatNaira(group.total_amount)}
                      </span>
                    </span>
                    {group.oldest_delivered_at && (
                      <span>
                        Oldest delivered {formatDate(group.oldest_delivered_at)}
                      </span>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => toggleExpanded(group.store_id)}
                    className="rounded border border-slate-300 px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50"
                  >
                    {expanded.has(group.store_id)
                      ? 'Hide orders'
                      : 'View orders'}
                  </button>
                  {canSettle && (
                    <button
                      type="button"
                      onClick={() => settleSeller(group)}
                      disabled={actingStoreId === group.store_id}
                      className="rounded bg-emerald-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-emerald-700 disabled:opacity-50"
                    >
                      {actingStoreId === group.store_id
                        ? '…'
                        : `Settle all (${group.order_count})`}
                    </button>
                  )}
                </div>
              </div>

              {expanded.has(group.store_id) && (
                <ul className="divide-y divide-slate-100 px-5 py-3 text-sm">
                  {group.orders.map((o) => (
                    <li
                      key={o.reference}
                      className="flex items-center justify-between gap-3 py-2"
                    >
                      <div className="min-w-0">
                        <Link
                          to={`/orders/${o.reference}`}
                          className="font-mono text-xs text-brand-600 hover:underline"
                        >
                          {o.reference}
                        </Link>
                        <div className="text-xs text-slate-500">
                          Delivered {formatDate(o.delivered_at)}
                          {o.customer_confirmed_at && (
                            <>
                              {' '}
                              · Confirmed {formatDate(o.customer_confirmed_at)}
                            </>
                          )}
                        </div>
                      </div>
                      <div className="text-sm font-medium text-slate-800">
                        {formatNaira(o.total_price)}
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </section>
          ))}
        </div>
      )}

      {!loading &&
        !error &&
        data &&
        (data.consultations?.data.length ?? 0) > 0 && (
          <div className="mt-6 space-y-3">
            <h2 className="text-sm font-semibold uppercase tracking-wide text-slate-500">
              Consultants ({data.consultations!.summary.consultant_count}) ·{' '}
              {formatNaira(data.consultations!.summary.total_amount)}
            </h2>
            {data.consultations!.data.map((group) => (
              <section
                key={group.consultant_id}
                className="rounded border border-slate-200 bg-white"
              >
                <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-100 px-5 py-4">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 text-sm">
                      <Link
                        to={`/users/${group.consultant_user_id}`}
                        className="font-semibold text-brand-600 hover:underline"
                      >
                        {group.consultant_name ||
                          `Consultant #${group.consultant_id}`}
                      </Link>
                      {group.consultant_email && (
                        <span className="truncate text-xs text-slate-500">
                          {group.consultant_email}
                        </span>
                      )}
                    </div>
                    <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-slate-500">
                      <span>
                        {group.appointment_count} consultation
                        {group.appointment_count === 1 ? '' : 's'} ·{' '}
                        <span className="font-medium text-slate-800">
                          {formatNaira(group.total_amount)}
                        </span>
                      </span>
                      {group.oldest_completed_at && (
                        <span>
                          Oldest completed{' '}
                          {formatDate(group.oldest_completed_at)}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() =>
                        toggleConsultantExpanded(group.consultant_id)
                      }
                      className="rounded border border-slate-300 px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50"
                    >
                      {expandedConsultants.has(group.consultant_id)
                        ? 'Hide appointments'
                        : 'View appointments'}
                    </button>
                    {canSettle && (
                      <button
                        type="button"
                        onClick={() => settleConsultant(group)}
                        disabled={actingConsultantId === group.consultant_id}
                        className="rounded bg-emerald-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-emerald-700 disabled:opacity-50"
                      >
                        {actingConsultantId === group.consultant_id
                          ? '…'
                          : `Settle all (${group.appointment_count})`}
                      </button>
                    )}
                  </div>
                </div>

                {expandedConsultants.has(group.consultant_id) && (
                  <ul className="divide-y divide-slate-100 px-5 py-3 text-sm">
                    {group.appointments.map((a) => (
                      <li
                        key={a.id}
                        className="flex items-center justify-between gap-3 py-2"
                      >
                        <div className="min-w-0">
                          <Link
                            to={`/consultations/${a.id}`}
                            className="font-mono text-xs text-brand-600 hover:underline"
                          >
                            #{a.id}
                          </Link>
                          <div className="text-xs text-slate-500">
                            Completed {formatDate(a.completed_at)}
                          </div>
                        </div>
                        <div className="text-sm font-medium text-slate-800">
                          {formatNaira(a.fee ?? '0')}
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </section>
            ))}
          </div>
        )}
    </div>
  );
}

function SummaryStat({
  label,
  value,
  tone,
}: {
  label: string;
  value: number | string;
  tone?: 'emerald';
}) {
  return (
    <div className="rounded border border-slate-200 bg-white px-3 py-2">
      <div className="text-[11px] uppercase tracking-wide text-slate-500">
        {label}
      </div>
      <div
        className={`text-sm font-semibold ${tone === 'emerald' ? 'text-emerald-700' : 'text-slate-800'}`}
      >
        {value}
      </div>
    </div>
  );
}

interface EscrowBalance {
  configured: boolean;
  reason: string | null;
  wallet_id: string | null;
  account_number: string | null;
  bank_name: string | null;
  bank_code: string | null;
  available_balance: number | null;
  ledger_balance: number | null;
  currency: string;
  fetched_at: string | null;
}

function EscrowBalanceCard() {
  const [data, setData] = useState<EscrowBalance | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.get<EscrowBalance>(
        '/back-office/settlements/escrow-balance'
      );
      setData(res.data);
    } catch (err: unknown) {
      setError(extractErrorMessage(err, 'Failed to load escrow balance'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  // Total owed across stores + consultants (we don't have direct access to
  // the parent's data here; the card stands alone for simplicity).
  return (
    <section className="mt-6 rounded-lg border border-slate-200 bg-gradient-to-br from-slate-50 to-white p-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">
            Platform escrow wallet
          </div>
          <h2 className="mt-0.5 text-lg font-semibold text-slate-800">
            Settlement float
          </h2>
          <p className="mt-0.5 text-xs text-slate-500">
            Live balance of the platform&apos;s Embedly settlement wallet.
            Confirm sufficient float before clicking settle.
          </p>
        </div>
        <button
          type="button"
          onClick={load}
          disabled={loading}
          className="rounded border border-slate-300 px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50"
        >
          {loading ? 'Refreshing…' : 'Refresh'}
        </button>
      </div>

      {error && (
        <div className="mt-3 rounded border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">
          {error}
        </div>
      )}

      {!loading && data && !data.configured && (
        <div className="mt-3 rounded border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
          {data.reason ?? 'Escrow wallet not configured.'}
        </div>
      )}

      {data?.configured && (
        <div className="mt-3 grid grid-cols-1 gap-4 md:grid-cols-3">
          <div>
            <div className="text-[10px] uppercase text-slate-500">
              Available
            </div>
            <div className="text-2xl font-bold text-emerald-700">
              {data.available_balance == null
                ? '—'
                : new Intl.NumberFormat('en-NG', {
                    style: 'currency',
                    currency: data.currency || 'NGN',
                    maximumFractionDigits: 0,
                  }).format(data.available_balance)}
            </div>
          </div>
          <div>
            <div className="text-[10px] uppercase text-slate-500">Ledger</div>
            <div className="text-base font-semibold text-slate-800">
              {data.ledger_balance == null
                ? '—'
                : new Intl.NumberFormat('en-NG', {
                    style: 'currency',
                    currency: data.currency || 'NGN',
                    maximumFractionDigits: 0,
                  }).format(data.ledger_balance)}
            </div>
          </div>
          <div className="min-w-0">
            <div className="text-[10px] uppercase text-slate-500">Account</div>
            <div className="text-sm font-mono text-slate-800 truncate">
              {data.account_number ?? '—'}
            </div>
            <div className="text-[11px] text-slate-500 truncate">
              {data.bank_name ?? ''}
              {data.bank_code && ` · ${data.bank_code}`}
            </div>
          </div>
        </div>
      )}

      {data?.configured && data.fetched_at && (
        <div className="mt-2 text-[10px] text-slate-400">
          As of {new Date(data.fetched_at).toLocaleTimeString()}
        </div>
      )}
    </section>
  );
}
